// /app/frontend/src/components/admin/CerebroDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiCpu, FiHardDrive, FiDatabase, FiActivity, FiAlertTriangle,
  FiTrendingUp, FiRefreshCw, FiCheck, FiZap, FiTarget,
  FiServer, FiWifi, FiClock, FiUsers
} from 'react-icons/fi';
import { 
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '@/services/api';
import FiliaisManager from './FiliaisManager';

const CerebroDashboard = () => {
  const [monitorData, setMonitorData] = useState(null);
  const [history, setHistory] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const loadMonitorData = useCallback(async () => {
    try {
      const res = await api.get('/cerebro/monitor');
      setMonitorData(res.data);
    } catch (error) {
      console.error('Erro ao carregar monitor:', error);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [monitorRes, historyRes, alertsRes, predictRes, recsRes, milestonesRes] = await Promise.allSettled([
        api.get('/cerebro/monitor'),
        api.get('/cerebro/history?days=7'),
        api.get('/cerebro/alerts'),
        api.get('/cerebro/predict'),
        api.get('/cerebro/recommendations'),
        api.get('/cerebro/milestones')
      ]);

      if (monitorRes.status === 'fulfilled') setMonitorData(monitorRes.value.data);
      if (historyRes.status === 'fulfilled') setHistory(historyRes.value.data);
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data);
      if (predictRes.status === 'fulfilled') setPredictions(predictRes.value.data);
      if (recsRes.status === 'fulfilled') setRecommendations(recsRes.value.data);
      if (milestonesRes.status === 'fulfilled') setMilestones(milestonesRes.value.data);
    } catch (error) {
      console.error('Erro ao carregar dados do Cérebro:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadMonitorData, 30000); // Atualiza a cada 30s
    return () => clearInterval(interval);
  }, [loadAllData, loadMonitorData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const getLevelColor = (level) => {
    const colors = {
      ok: 'text-green-500',
      watch: 'text-yellow-500',
      warning: 'text-orange-500',
      critical: 'text-red-500',
      emergency: 'text-red-700'
    };
    return colors[level] || 'text-gray-500';
  };

  const getLevelBg = (level) => {
    const colors = {
      ok: 'bg-green-100',
      watch: 'bg-yellow-100',
      warning: 'bg-orange-100',
      critical: 'bg-red-100',
      emergency: 'bg-red-200'
    };
    return colors[level] || 'bg-gray-100';
  };

  const getProgressColor = (percent) => {
    if (percent >= 85) return 'bg-red-500';
    if (percent >= 70) return 'bg-orange-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="cerebro-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando Cérebro Inteligente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cerebro-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-3xl">🧠</span>
            Cérebro Inteligente
          </h2>
          <p className="text-gray-500 text-sm">
            Monitoramento e previsões para 300 filiais
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          data-testid="refresh-button"
        >
          <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {/* Status Geral */}
      {monitorData && (
        <div className={`p-4 rounded-xl ${getLevelBg(monitorData.overall_status?.level)} border-l-4 ${
          monitorData.overall_status?.level === 'ok' ? 'border-green-500' :
          monitorData.overall_status?.level === 'watch' ? 'border-yellow-500' :
          monitorData.overall_status?.level === 'warning' ? 'border-orange-500' :
          'border-red-500'
        }`} data-testid="overall-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{monitorData.overall_status?.emoji}</span>
              <div>
                <h3 className={`font-bold text-lg ${getLevelColor(monitorData.overall_status?.level)}`}>
                  {monitorData.overall_status?.message}
                </h3>
                <p className="text-gray-600 text-sm">
                  {monitorData.capacity?.current_filials} de {monitorData.capacity?.max_filials} filiais 
                  ({monitorData.capacity?.usage_percent}%)
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-800">
                {monitorData.capacity?.estimated_capacity}
              </p>
              <p className="text-xs text-gray-500">Capacidade estimada</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs de Navegação */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'overview', label: 'Visão Geral', icon: FiActivity },
          { key: 'filiais', label: 'Filiais', icon: FiUsers },
          { key: 'metrics', label: 'Métricas', icon: FiCpu },
          { key: 'alerts', label: 'Alertas', icon: FiAlertTriangle, badge: alerts?.total_alerts },
          { key: 'predictions', label: 'Previsões', icon: FiTrendingUp },
          { key: 'milestones', label: 'Milestones', icon: FiTarget }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              activeSection === tab.key 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.badge > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Conteúdo das Tabs */}
      {activeSection === 'overview' && monitorData && (
        <div className="space-y-6">
          {/* Cards de Métricas Principais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {monitorData.metrics?.slice(0, 4).map((metric, idx) => (
              <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border" data-testid={`metric-card-${metric.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">{metric.name}</span>
                  <span className="text-xl">{metric.emoji}</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {metric.current_value}{metric.unit === '%' ? '%' : ''} 
                  <span className="text-sm text-gray-400">/ {metric.max_value}</span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${getProgressColor(metric.percent_used)}`}
                    style={{ width: `${Math.min(100, metric.percent_used)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 ${getLevelColor(metric.level)}`}>
                  {metric.percent_used}% utilizado
                </p>
              </div>
            ))}
          </div>

          {/* Recomendações */}
          {recommendations && recommendations.recommendations?.length > 0 && (
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FiZap className="text-yellow-500" />
                Recomendações Inteligentes
              </h3>
              <div className="space-y-3">
                {recommendations.recommendations.slice(0, 3).map((rec, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg ${
                      rec.priority === 'alta' ? 'bg-red-50 border-l-4 border-red-500' :
                      rec.priority === 'média' ? 'bg-yellow-50 border-l-4 border-yellow-500' :
                      'bg-green-50 border-l-4 border-green-500'
                    }`}
                    data-testid={`recommendation-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">{rec.title}</h4>
                        <p className="text-sm text-gray-600">{rec.description}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        rec.priority === 'alta' ? 'bg-red-200 text-red-700' :
                        rec.priority === 'média' ? 'bg-yellow-200 text-yellow-700' :
                        'bg-green-200 text-green-700'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                    {rec.actions && (
                      <ul className="mt-2 text-sm text-gray-500">
                        {rec.actions.slice(0, 2).map((action, i) => (
                          <li key={i} className="flex items-center gap-1">
                            <FiCheck className="text-green-500" size={12} />
                            {action}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Próximo Milestone */}
          {monitorData.next_milestone && (
            <div className="bg-gradient-to-r from-red-600 to-red-800 p-6 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <FiTarget />
                    Próximo Milestone: {monitorData.next_milestone.name}
                  </h3>
                  <p className="text-red-100 text-sm">
                    Meta: {monitorData.next_milestone.target_filials} filiais
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">
                    {monitorData.next_milestone.filials_remaining}
                  </p>
                  <p className="text-red-100 text-sm">filiais restantes</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="h-3 bg-red-900 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full"
                    style={{ 
                      width: `${((monitorData.next_milestone.target_filials - monitorData.next_milestone.filials_remaining) / monitorData.next_milestone.target_filials) * 100}%` 
                    }}
                  />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-red-100 text-xs">Requisitos</p>
                  <ul className="text-sm mt-1">
                    {monitorData.next_milestone.requirements?.slice(0, 2).map((req, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <FiServer size={12} />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-right">
                  <p className="text-red-100 text-xs">Custo estimado</p>
                  <p className="text-2xl font-bold">
                    R$ {monitorData.next_milestone.monthly_cost_estimate?.toLocaleString('pt-BR')}
                  </p>
                  <p className="text-red-100 text-xs">/mês</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab de Gerenciamento de Filiais */}
      {activeSection === 'filiais' && (
        <div className="animate-fadeIn">
          <FiliaisManager />
        </div>
      )}

      {activeSection === 'metrics' && monitorData && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <h3 className="font-bold text-gray-800 mb-4">Todas as Métricas</h3>
            <div className="space-y-4">
              {monitorData.metrics?.map((metric, idx) => (
                <div key={idx} className="border-b pb-4 last:border-0" data-testid={`full-metric-${metric.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{metric.emoji}</span>
                      <div>
                        <h4 className="font-medium text-gray-800">{metric.name}</h4>
                        <p className="text-sm text-gray-500">
                          {metric.current_value} / {metric.max_value} {metric.unit}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelBg(metric.level)} ${getLevelColor(metric.level)}`}>
                      {metric.level.toUpperCase()}
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${getProgressColor(metric.percent_used)}`}
                      style={{ width: `${Math.min(100, metric.percent_used)}%` }}
                    />
                  </div>
                  {metric.recommendation && (
                    <p className="text-sm text-orange-600 mt-2 flex items-center gap-1">
                      <FiAlertTriangle size={14} />
                      {metric.recommendation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Métricas Brutas */}
          {monitorData.raw_metrics && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FiServer />
                  Sistema
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">CPU Cores</span>
                    <span className="font-medium">{monitorData.raw_metrics.system?.cpu_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Memória Total</span>
                    <span className="font-medium">{monitorData.raw_metrics.system?.memory_total_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Disco Total</span>
                    <span className="font-medium">{monitorData.raw_metrics.system?.disk_total_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Conexões WebSocket</span>
                    <span className="font-medium">{monitorData.raw_metrics.system?.websocket_connections}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm border">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FiDatabase />
                  MongoDB
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Storage</span>
                    <span className="font-medium">{monitorData.raw_metrics.database?.storage_size_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Conexões Ativas</span>
                    <span className="font-medium">{monitorData.raw_metrics.database?.connections_current}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total de Usuários</span>
                    <span className="font-medium">{monitorData.raw_metrics.database?.users_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total de Pedidos</span>
                    <span className="font-medium">{monitorData.raw_metrics.database?.orders_count}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'alerts' && alerts && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <FiAlertTriangle className="text-orange-500" />
                Alertas Ativos
              </h3>
              <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                {alerts.total_alerts} alertas
              </span>
            </div>
            
            {alerts.alerts?.length > 0 ? (
              <div className="space-y-3">
                {alerts.alerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg ${getLevelBg(alert.level)} border`}
                    data-testid={`alert-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{alert.emoji}</span>
                        <div>
                          <h4 className="font-medium text-gray-800">{alert.metric}</h4>
                          <p className={`text-sm ${getLevelColor(alert.level)}`}>
                            {alert.current_percent}% utilizado (limite: {alert.threshold}%)
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getLevelBg(alert.level)} ${getLevelColor(alert.level)}`}>
                        {alert.level.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-3 bg-white/50 p-2 rounded text-sm">
                      <p className="font-medium text-gray-700">Ação recomendada:</p>
                      <p className="text-gray-600">{alert.action}</p>
                    </div>
                    {alert.upgrade_at_filials && (
                      <p className="mt-2 text-xs text-gray-500">
                        Upgrade recomendado em {alert.upgrade_at_filials} filiais → {alert.upgrade_to}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FiCheck className="mx-auto text-4xl text-green-500 mb-2" />
                <p>Nenhum alerta ativo</p>
                <p className="text-sm">Sistema operando normalmente</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'predictions' && predictions && (
        <div className="space-y-4">
          {predictions.message ? (
            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
              <p className="text-yellow-700">{predictions.message}</p>
            </div>
          ) : (
            <>
              {/* Taxa de Crescimento */}
              <div className="bg-white p-4 rounded-xl shadow-sm border">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FiTrendingUp className="text-green-500" />
                  Taxa de Crescimento
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-4xl font-bold text-green-600">
                      {predictions.current_growth_rate?.filials_per_day}
                    </p>
                    <p className="text-sm text-gray-500">filiais/dia</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-4xl font-bold text-blue-600">
                      {predictions.current_growth_rate?.filials_per_month}
                    </p>
                    <p className="text-sm text-gray-500">filiais/mês</p>
                  </div>
                </div>
              </div>

              {/* Previsões de Filiais */}
              {predictions.filial_predictions?.length > 0 && (
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <FiTarget className="text-red-500" />
                    Previsão de Milestones
                  </h3>
                  <div className="space-y-3">
                    {predictions.filial_predictions.map((pred, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-800">{pred.milestone}</h4>
                          <p className="text-sm text-gray-500">
                            Em aproximadamente {pred.days_until} dias
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-800">{pred.estimated_date}</p>
                          <p className="text-sm text-gray-500">
                            ~R$ {pred.monthly_cost_estimate}/mês
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Previsões de Recursos */}
              {predictions.resource_predictions?.length > 0 && (
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <FiClock className="text-orange-500" />
                    Previsão de Upgrades
                  </h3>
                  <div className="space-y-3">
                    {predictions.resource_predictions.map((pred, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border-l-4 ${
                          pred.urgency === 'alta' ? 'bg-red-50 border-red-500' :
                          pred.urgency === 'média' ? 'bg-yellow-50 border-yellow-500' :
                          'bg-green-50 border-green-500'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-800">{pred.metric}</h4>
                            <p className="text-sm text-gray-500">
                              Atingirá {pred.threshold_percent}% ({pred.threshold_level}) em {pred.days_until} dias
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            pred.urgency === 'alta' ? 'bg-red-200 text-red-700' :
                            pred.urgency === 'média' ? 'bg-yellow-200 text-yellow-700' :
                            'bg-green-200 text-green-700'
                          }`}>
                            {pred.urgency}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Ação:</strong> {pred.action_required}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Data estimada: {pred.estimated_date}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeSection === 'milestones' && milestones && (
        <div className="space-y-4">
          {/* Progresso Geral */}
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Progresso para 300 Filiais</h3>
              <span className="text-2xl font-bold text-red-600">
                {milestones.progress_percent}%
              </span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all"
                style={{ width: `${milestones.progress_percent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-500">
              <span>{milestones.current_filials} filiais</span>
              <span>{milestones.target_filials} filiais</span>
            </div>
          </div>

          {/* Timeline de Milestones */}
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <h3 className="font-bold text-gray-800 mb-4">Roadmap de Infraestrutura</h3>
            <div className="space-y-4">
              {milestones.milestones?.map((ms, idx) => (
                <div 
                  key={idx} 
                  className={`relative pl-8 pb-4 border-l-2 ${
                    ms.status === 'completed' ? 'border-green-500' :
                    ms.status === 'current' ? 'border-red-500' :
                    'border-gray-300'
                  }`}
                  data-testid={`milestone-${ms.name}`}
                >
                  {/* Indicador */}
                  <div className={`absolute left-0 top-0 w-4 h-4 rounded-full -translate-x-1/2 ${
                    ms.status === 'completed' ? 'bg-green-500' :
                    ms.status === 'current' ? 'bg-red-500 animate-pulse' :
                    'bg-gray-300'
                  }`}>
                    {ms.status === 'completed' && (
                      <FiCheck className="text-white w-full h-full p-0.5" />
                    )}
                  </div>

                  <div className={`p-4 rounded-lg ${
                    ms.status === 'completed' ? 'bg-green-50' :
                    ms.status === 'current' ? 'bg-red-50 border-2 border-red-200' :
                    'bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">
                        {ms.name}
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          ({ms.target_filials} filiais)
                        </span>
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        ms.status === 'completed' ? 'bg-green-200 text-green-700' :
                        ms.status === 'current' ? 'bg-red-200 text-red-700' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {ms.status === 'completed' ? 'Concluído' :
                         ms.status === 'current' ? 'Atual' : 'Próximo'}
                      </span>
                    </div>

                    {ms.status === 'current' && (
                      <div className="mb-3">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full"
                            style={{ width: `${ms.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{ms.progress}% concluído</p>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Requisitos</p>
                        <ul className="text-sm space-y-1">
                          {ms.requirements?.map((req, i) => (
                            <li key={i} className="flex items-start gap-1 text-gray-600">
                              <FiCheck className={`mt-0.5 flex-shrink-0 ${
                                ms.status === 'completed' ? 'text-green-500' : 'text-gray-400'
                              }`} size={14} />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">Custo estimado</p>
                        <p className="text-xl font-bold text-gray-800">
                          {ms.monthly_cost_estimate}
                        </p>
                        <p className="text-xs text-gray-500">/mês</p>
                      </div>
                    </div>

                    {ms.optional?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Opcional</p>
                        <div className="flex flex-wrap gap-2">
                          {ms.optional.map((opt, i) => (
                            <span key={i} className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                              {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Histórico (Gráfico) */}
      {history && history.history?.length > 1 && activeSection === 'overview' && (
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FiActivity />
            Histórico de Uso (7 dias)
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={history.history.slice(-50)}>
              <defs>
                <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DC143C" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#DC143C" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="memGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', hour: '2-digit' })}
                fontSize={10}
              />
              <YAxis domain={[0, 100]} fontSize={10} />
              <Tooltip 
                labelFormatter={(v) => new Date(v).toLocaleString('pt-BR')}
                formatter={(v) => [`${v.toFixed(1)}%`]}
              />
              <Area 
                type="monotone" 
                dataKey="metrics.cpu_percent" 
                stroke="#DC143C" 
                fill="url(#cpuGradient)"
                name="CPU"
              />
              <Area 
                type="monotone" 
                dataKey="metrics.memory_percent" 
                stroke="#3B82F6" 
                fill="url(#memGradient)"
                name="Memória"
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-2 text-sm">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded" />
              CPU
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              Memória
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CerebroDashboard;
